import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { addStaffDocument, getStaffPayrollDetails, removeStaffPayrollDocument } from "../services/staff.service";
import Swal from "sweetalert2";

export const useStaffPayroll = ({ supplier, staffAssignmentId, removeSelectedStaff }) => {
    const queryClient = useQueryClient();

    const [payrollModal, setPayrollModal] = useState(false);

    const queryKey = ['staffPayroll', supplier._id, staffAssignmentId];

    const {
        data,
        isError,
        error,
        isLoading,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => getStaffPayrollDetails({ staffAssignmentId }),
        enabled: !!supplier && !!staffAssignmentId,
        staleTime: 5 * 60 * 1000, // 5 minutos
        cacheTime: 10 * 60 * 1000, // 10 minutos
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        onError: (error) => {
            console.error('Error fetching staff payroll:', error.response?.data?.message || error.message || 'Error getting staff details');
        },
    });

    useEffect(() => {
        if (!isLoading && error) {
            Swal.fire({
                icon: 'error',
                title: 'Error loading staff details',
                text: error.response?.data?.message || error.message || 'An unexpected error occurred',
                showConfirmButton: true,
                confirmButtonText: 'OK',
            });

            removeSelectedStaff?.()

            return
        }
        
        if (isLoading) {
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            return
        }

        if (!isLoading && data) {
            setPayrollModal(true)
            Swal.close()
        }
    }, [isLoading, error, data, removeSelectedStaff]);

    const addPayrollDocumentMutation = useMutation({
        mutationFn: ({ staffAssignmentId, file }) => 
            addStaffDocument({ staffAssignmentId, file }),
        onSuccess: () => {
            // Invalidar las queries relacionadas
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries({
                queryKey: ['staffList']
            });
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || error.message || 'An error occurred while changing the profile picture.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            
            console.error('Error adding staff document:', error.response?.data?.message || error.message);
        }
    })

    const removeStaffPayrollDocumentMutation = useMutation({
        mutationFn: ({ staffAssignmentId, documentId }) =>
            removeStaffPayrollDocument({ staffAssignmentId, documentId }),
        onSuccess: () => {
            // Invalidar las queries relacionadas
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries({
                queryKey: ['staffList']
            });
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || error.message || 'An error occurred while removing the document.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            console.error('Error removing staff document:', error.response?.data?.message || error.message);
        }
    })

    const handleCloseModal = () => {
        setPayrollModal(false);
    }

    const handleAddStaffDocument = (staffAssignmentId, file) => {
        addPayrollDocumentMutation.mutate({ staffAssignmentId, file });
    }

    const handleRemoveStaffDocument = (staffAssignmentId, documentId) => {
        removeStaffPayrollDocumentMutation.mutate({ staffAssignmentId, documentId });
    }

    const dataToReturn = {
        data,
        isError,
        error,
        isLoading,
        refetch,

        // states
        payrollModal,

        // handlers
        handleCloseModal,
        handleAddStaffDocument,
        handleRemoveStaffDocument
    }

    return dataToReturn;
}

export default useStaffPayroll;